import React, { useContext, useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import PropTypes from 'prop-types';

import { WarehouseInventoryV2Api } from 'api/warehouseInventoryV2';
import { UserContext } from 'contexts/UserContext';
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from 'shadcn/components/ui/Table';

import { getCachedLots, setCachedLots } from './bulkMovePalletsLotsCache';

const warehouseInventoryV2Api = new WarehouseInventoryV2Api();

function mapLotsResponse(lotsData) {
    return lotsData.map((lot) => ({
        ...lot,
        looseUnits: lot.numLooseUnits,
        cases: lot.numCases,
        partnerSku: lot.partnerSku ?? 'N/A',
    }));
}

function BulkMovePalletsExpansionTable({ palletId }) {
    const cachedLots = getCachedLots(palletId);
    const [lots, setLots] = useState(cachedLots ?? []);
    const [loading, setLoading] = useState(!cachedLots);
    const { user } = useContext(UserContext);

    // API call to GET data
    const getLotDataForPallet = async () => {
        const cached = getCachedLots(palletId);
        if (cached) {
            setLots(cached);
            setLoading(false);
            return;
        }

        setLoading(true);
        const searchValues = [{ searchBy: 'palletId', searchTerm: palletId }];
        const result = await warehouseInventoryV2Api.getLots(user.facilityID, searchValues);
        if (result.status) {
            const mappedLots = mapLotsResponse(result.data.lots);
            setCachedLots(palletId, mappedLots);
            setLots(mappedLots);
        } else {
            setLots([]);
        }
        setLoading(false);
    };

    useEffect(() => {
        getLotDataForPallet();
    }, [palletId]);

    if (loading) {
        return (
            <div className="flex items-center justify-center py-4">
                <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
            </div>
        );
    }

    return (
        <div className="bg-gray-50 px-8 py-4">
            <div className="max-h-[300px] w-full overflow-x-auto overflow-y-auto">
                <table className="w-max min-w-full caption-bottom text-sm">
                    <TableHeader>
                        <TableRow className="hover:bg-transparent border-b">
                            <TableHead className="bg-gray-100 sticky top-0 z-10 font-semibold">LOT ID</TableHead>
                            <TableHead className="bg-gray-100 sticky top-0 z-10 font-semibold">ITEM NAME</TableHead>
                            <TableHead className="bg-gray-100 sticky top-0 z-10 font-semibold">EXTERNAL SKU</TableHead>
                            <TableHead className="bg-gray-100 sticky top-0 z-10 font-semibold">PARTNER SKU</TableHead>
                            <TableHead className="bg-gray-100 sticky top-0 z-10 font-semibold">CASES</TableHead>
                            <TableHead className="bg-gray-100 sticky top-0 z-10 font-semibold">LOOSE UNITS</TableHead>
                            <TableHead className="bg-gray-100 sticky top-0 z-10 font-semibold">TOTAL UNITS</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {lots.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={7} className="text-center py-4 text-gray-500">
                                    No lots found for this pallet
                                </TableCell>
                            </TableRow>
                        ) : (
                            lots.map((lot, index) => (
                                <TableRow key={lot.lotId} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                    <TableCell>{lot.lotId}</TableCell>
                                    <TableCell>{lot.itemName}</TableCell>
                                    <TableCell>{lot.externalSku}</TableCell>
                                    <TableCell>{lot.partnerSku}</TableCell>
                                    <TableCell>{lot.cases}</TableCell>
                                    <TableCell>{lot.looseUnits}</TableCell>
                                    <TableCell>{lot.totalUnits}</TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </table>
            </div>
        </div>
    );
}

BulkMovePalletsExpansionTable.propTypes = {
    palletId: PropTypes.string.isRequired,
};

export { BulkMovePalletsExpansionTable };
